import React, { useEffect, useCallback } from 'react'
import { requestCommon, jsonResponseExtractor } from 'live/lib/utils'
import i18n from 'live/services/i18n'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import { useWebSettingsState } from 'live/settings/useWebSettingsState'
import globalNotification from 'live/services/global-notification'
import FormGroup from 'live/components/forms/ui/form-group'

const pipesVersion = (Live && Live.pipesVersion) || ''

const BASE_URL = '/services/plugin-annotations/timeline/config'

type QueryParams = {
    extendHistory: string
}

const TimelineSettings = () => {
    const [queryParams, setQueryParams] = React.useState<QueryParams>({ extendHistory: '' })
    const { state, actions } = useWebSettingsState(queryParams)

    const fetchItems = useCallback(() => {
        api.get()
            .then(jsonResponseExtractor)
            .then(queryParams => {
                setQueryParams((queryParams as unknown) as QueryParams)
                actions.setPersistedData(queryParams)
            })
    }, [actions])

    useEffect(fetchItems, [])

    const onSave = React.useCallback(() => {
        actions.submit()

        api.persist(queryParams)
            .then(jsonResponseExtractor)
            .then((queryParams) => {
                setQueryParams((queryParams as unknown) as QueryParams)
                actions.setPersistedData(queryParams)

                actions.saveSuccess(queryParams)
                globalNotification.success(i18n('Settings successfully saved'))
            })
            .catch(async r => {
                actions.setError(i18n('There was an error saving the query params'))
            })
    }, [actions, queryParams])

    return (
        <WebSettingsCard
            name="export-settings"
            className="live-feature mb-15"
            open={state.open}
            error={(state.submitted && state.open) ? state.error || undefined : undefined}
            saved={state.open && state.saved}
            save={onSave}
            toggle={actions.toggle}
            onCancel={() => {
                actions.setError()
                setQueryParams(state.persistedData)
            }}
        >
            <div style={{ marginBottom: '35px' }}>
                <div className="live-feature__header">
                    <h2 className="flex-1">{i18n('Query params')}</h2>
                </div>

                <FormGroup
                    hideInput={!state.open}
                    label={i18n('Extend history')}
                    validationState={(state.open && state.submitted) ? (state.error ? 'error' : 'success') : undefined}
                    className="form-group--small-input"
                    type="text"
                    value={queryParams.extendHistory}
                    onChange={({ target }) => {
                        setQueryParams({
                            extendHistory: target.value
                        })
                    }}
                />

                <span className="help-block">
                    {i18n('extend_history_help_block', { pipesVersion: `${pipesVersion}` })}
                </span>
            </div>
        </WebSettingsCard>
    )
}

const api = {
    get() {
        return fetch(BASE_URL, {
            credentials: 'same-origin'
        })
    },
    persist(settings: QueryParams) {
        return fetch(BASE_URL, {
            method: 'POST',
            ...requestCommon,
            body: JSON.stringify(settings)
        })
    }
}

export default {
    persist: api.persist,
    configComponent: TimelineSettings,
    section: i18n('Annotations settings'),
    name: i18n('Query params')
}


